import type { ColumnDef } from '@tanstack/react-table';

import { cn } from '@nesso/ui/lib/utils';

import type {
    PracticeMemberUsageOutput,
    PracticeMemberUsageSortBy,
    PracticeMemberUsageSortDirection,
} from '@/entities/reporting';

import { formatElapsedTime } from '../../../model/format-elapsed-time';
import { getAriaSort } from '../../../model/get-aria-sort';
import { ReportingPagePracticeMemberUsageTableImpersonateButton as ImpersonateButton } from './reporting-page-practice-member-usage-table-impersonate-button';
import { ReportingPagePracticeMemberUsageTableNameCell as NameCell } from './reporting-page-practice-member-usage-table-name-cell';
import { ReportingPagePracticeMemberUsageTableSortHeader as SortHeader } from './reporting-page-practice-member-usage-table-sort-header';

export type PracticeMemberUsageRow = PracticeMemberUsageOutput['practiceMembers'][number];

// Percent of the table's own `min-w-[997px]` floor, not `columnDef.size` — TanStack's
// `size`/`getSize()` only ever emits px, which would pin each column at a fixed width instead.
export const NAME_COLUMN_WIDTH = 'w-[22.07%]';
export const SCROLLABLE_COLUMN_WIDTH = 'w-[12.99%]';

// One `<table>` with per-cell `sticky` keeps every row's height in sync; the z-index stack
// below makes the header win any seam overlap.
export const HEAD_CELL = 'sticky top-0 z-40 font-bold text-muted-foreground';
export const CORNER_CELL = 'sticky left-0 z-50';
// Inherits the row's own bg (set on the row), which is a solid color-mix rather than an alpha
// hover tint, so this pinned cell stays opaque against scroll bleed-through in both states.
export const BODY_NAME_CELL = 'sticky left-0 z-10 bg-inherit';
export const GROUP_DIVIDER = 'border-r border-border';
export const NOTE_COUNT_CELL = 'text-right font-bold';

// shadow/lg adapted to the x axis: a downward shadow applied per cell would stripe between rows.
export const NAME_COLUMN_SCROLL_SHADOW =
    'shadow-[4px_0_6px_-4px_rgba(0,0,0,0.1),10px_0_15px_-3px_rgba(0,0,0,0.1)]';

type BuildColumnsArgs = {
    sortBy: PracticeMemberUsageSortBy;
    sortDirection: PracticeMemberUsageSortDirection;
    onSortChange: (column: PracticeMemberUsageSortBy) => void;
    isScrolled: boolean;
};

export function buildPracticeMemberUsageTableColumns({
    sortBy,
    sortDirection,
    onSortChange,
    isScrolled,
}: BuildColumnsArgs): ColumnDef<PracticeMemberUsageRow>[] {
    const sortHeaderProps = { sortBy, sortDirection, onSort: onSortChange };

    return [
        {
            id: 'name',
            header: () => <SortHeader label="Name" column="name" {...sortHeaderProps} />,
            cell: ({ row }) => <NameCell name={row.original.name} email={row.original.email} />,
            meta: {
                // Scroll shadow omitted here: a virtualized row's cached `columnDef` doesn't pick up
                // this rebuilt meta, so the body cell gets it from `isScrolled` in the row instead.
                className: cn(BODY_NAME_CELL, NAME_COLUMN_WIDTH),
                headerClassName: cn(
                    HEAD_CELL,
                    CORNER_CELL,
                    'bg-muted',
                    NAME_COLUMN_WIDTH,
                    isScrolled && NAME_COLUMN_SCROLL_SHADOW,
                ),
                ariaSort: getAriaSort('name', sortBy, sortDirection),
            },
        },
        {
            id: 'lastLoginAt',
            header: () => (
                <SortHeader label="Last log in" column="lastLoginAt" {...sortHeaderProps} />
            ),
            cell: ({ row }) => formatElapsedTime(row.original.lastLoginAt),
            meta: {
                className: SCROLLABLE_COLUMN_WIDTH,
                headerClassName: cn(HEAD_CELL, SCROLLABLE_COLUMN_WIDTH),
                ariaSort: getAriaSort('lastLoginAt', sortBy, sortDirection),
            },
        },
        {
            id: 'lastSessionAt',
            header: () => (
                <SortHeader label="Last session" column="lastSessionAt" {...sortHeaderProps} />
            ),
            cell: ({ row }) => formatElapsedTime(row.original.lastSessionAt),
            meta: {
                className: cn(GROUP_DIVIDER, SCROLLABLE_COLUMN_WIDTH),
                headerClassName: cn(HEAD_CELL, GROUP_DIVIDER, SCROLLABLE_COLUMN_WIDTH),
                ariaSort: getAriaSort('lastSessionAt', sortBy, sortDirection),
            },
        },
        {
            id: 'allNotes',
            header: () => (
                <SortHeader label="All notes" column="allNotes" align="end" {...sortHeaderProps} />
            ),
            cell: ({ row }) => row.original.allNotes,
            meta: {
                className: cn(NOTE_COUNT_CELL, SCROLLABLE_COLUMN_WIDTH),
                headerClassName: cn(HEAD_CELL, 'text-right', SCROLLABLE_COLUMN_WIDTH),
                ariaSort: getAriaSort('allNotes', sortBy, sortDirection),
            },
        },
        {
            id: 'last30Days',
            header: () => (
                <SortHeader
                    label="Last 30 days"
                    column="last30Days"
                    align="end"
                    {...sortHeaderProps}
                />
            ),
            cell: ({ row }) => row.original.last30Days,
            meta: {
                className: cn(NOTE_COUNT_CELL, SCROLLABLE_COLUMN_WIDTH),
                headerClassName: cn(HEAD_CELL, 'text-right', SCROLLABLE_COLUMN_WIDTH),
                ariaSort: getAriaSort('last30Days', sortBy, sortDirection),
            },
        },
        {
            id: 'last7Days',
            header: () => (
                <SortHeader
                    label="Last 7 days"
                    column="last7Days"
                    align="end"
                    {...sortHeaderProps}
                />
            ),
            cell: ({ row }) => row.original.last7Days,
            meta: {
                className: cn(NOTE_COUNT_CELL, GROUP_DIVIDER, SCROLLABLE_COLUMN_WIDTH),
                headerClassName: cn(
                    HEAD_CELL,
                    GROUP_DIVIDER,
                    'text-right',
                    SCROLLABLE_COLUMN_WIDTH,
                ),
                ariaSort: getAriaSort('last7Days', sortBy, sortDirection),
            },
        },
        {
            id: 'actions',
            header: () => <span className="sr-only">Actions</span>,
            cell: ({ row }) => (
                <div className="flex items-center justify-center">
                    {/* A nameless member falls back to email so the label never reads "Sign in as ". */}
                    <ImpersonateButton memberName={row.original.name || row.original.email} />
                </div>
            ),
            meta: {
                className: cn('py-2 pr-2 pl-4', SCROLLABLE_COLUMN_WIDTH),
                headerClassName: cn(HEAD_CELL, SCROLLABLE_COLUMN_WIDTH),
            },
        },
    ];
}
